/**
 * The favorite teams manager's rules, with no renderer and no fetch
 * (ISSUE-1399). v1's Favorites screen listed the fan's teams with their league
 * and searched teams by name across leagues. Typed text of two letters or
 * more searches every league at once through sports-api's
 * /search/teams-players (ISSUE-1380); a shorter or empty field browses one
 * league's teams from the league picker, filtered here by name.
 */
import type { typeCatalogSportInput } from "../scores/leagueSelectorRowModel";

export const TEAMS_HEADING = "Favorite teams";
export const TEAMS_SUB =
  "Teams you follow get their scores up top and send you alerts.";
export const YOUR_TEAMS_TITLE = "Your teams";
export const YOUR_TEAMS_EMPTY = "You're not following any teams yet.";
export const ADD_TEAMS_TITLE = "Add a team";
export const LEAGUE_PICKER_LABEL = "League";
export const LEAGUE_PICKER_PLACEHOLDER = "Pick a league";
export const TEAM_SEARCH_LABEL = "Search teams";
export const TEAM_SEARCH_PLACEHOLDER = "Team or city, any league";
export const TEAMS_LOADING_LABEL = "Loading teams";
export const NO_TEAM_MATCH = "No team matches that. Check the spelling.";
export const PICK_LEAGUE_FIRST = "Pick a league to see its teams.";
export const FOLLOW_LABEL = "Follow";
export const FOLLOWING_LABEL = "Following";
export const REMOVE_LABEL = "Remove";
export const TEAMS_LOAD_FAILED = "We couldn't load teams just now.";
export const SEARCH_ANY_LEAGUE_FAILED = "We couldn't search teams just now.";
/** The cross-league search asks the server from this many letters. */
export const TEAM_SEARCH_MIN_LENGTH = 2;
/** How long typing pauses before the cross-league search asks. */
export const TEAM_SEARCH_DEBOUNCE_MS = 350;

/**
 * The text worth sending to the cross-league search, or null while it is
 * under two letters (the league picker shows instead).
 */
export function searchableTeamQuery(raw: string): string | null {
  const trimmed = raw.trim().replace(/\s+/g, " ");
  return trimmed.length >= TEAM_SEARCH_MIN_LENGTH ? trimmed : null;
}
export const TEAMS_ROUTE = "/account/teams";

export type typeLeagueOption = { value: string; label: string };

/**
 * Every league a fan can pick a team from, followed leagues first in the
 * fan's own order, then the rest by sport and league order. Coming soon and
 * hidden leagues have no teams to show, so they are left out.
 */
export function buildTeamLeagueOptions(input: {
  sports: typeCatalogSportInput[];
  followedLeagueIds: readonly string[];
}): typeLeagueOption[] {
  const all: typeLeagueOption[] = [];
  for (const sport of input.sports) {
    for (const league of sport.leagues) {
      if (league.visibilityState !== "live") continue;
      all.push({ value: league.leagueId, label: league.shortName });
    }
  }
  const byId = new Map(all.map((option) => [option.value, option]));
  const followed: typeLeagueOption[] = [];
  for (const leagueId of input.followedLeagueIds) {
    const option = byId.get(leagueId);
    if (option !== undefined && !followed.includes(option)) {
      followed.push(option);
    }
  }
  return [...followed, ...all.filter((option) => !followed.includes(option))];
}

/** One league's short name by id, for a favorite team's subtitle. */
export function leagueShortNamesById(
  sports: typeCatalogSportInput[],
): Map<string, string> {
  const names = new Map<string, string>();
  for (const sport of sports) {
    for (const league of sport.leagues) {
      names.set(league.leagueId, league.shortName);
    }
  }
  return names;
}

export type typeSearchableTeam = {
  teamId: string;
  name: string;
  marketName: string;
  nickname: string;
  abbreviation: string;
};

/**
 * The league's teams that match what the fan typed, by name, city,
 * nickname or abbreviation, sorted by name. An empty query keeps them all.
 */
export function filterTeams<T extends typeSearchableTeam>(
  teams: readonly T[],
  query: string,
): T[] {
  const needle = query.trim().toLowerCase();
  const matches =
    needle.length === 0
      ? [...teams]
      : teams.filter((team) =>
          [team.name, team.marketName, team.nickname, team.abbreviation].some(
            (field) => field.toLowerCase().includes(needle),
          ),
        );
  return matches.sort((left, right) => left.name.localeCompare(right.name));
}
